(() => {
  if (window.__mdTestimonialsInit) return;
  window.__mdTestimonialsInit = true;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const clampObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => updateClamp(entry.target.closest('[data-md-clamp]')));
  });

  function updateClamp(wrap) {
    if (!wrap) return;
    const quote = wrap.querySelector('blockquote');
    const toggle = wrap.querySelector('[data-md-clamp-toggle]');
    if (!quote || !toggle) return;
    if (wrap.classList.contains('is-expanded')) {
      toggle.hidden = false;
      return;
    }
    toggle.hidden = quote.scrollHeight <= quote.clientHeight + 2;
  }

  function initClamps(root) {
    root.querySelectorAll('[data-md-clamp]').forEach((wrap) => {
      if (wrap.dataset.mdClampReady) return;
      wrap.dataset.mdClampReady = 'true';
      const quote = wrap.querySelector('blockquote');
      if (quote) clampObserver.observe(quote);
      updateClamp(wrap);
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-md-clamp-toggle]');
    if (!toggle) return;
    const wrap = toggle.closest('[data-md-clamp]');
    const expanded = !wrap.classList.contains('is-expanded');
    wrap.classList.toggle('is-expanded', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    const label = toggle.querySelector('[data-md-clamp-label]');
    if (label) label.textContent = expanded ? 'Show less' : 'Read more';
    if (!expanded) updateClamp(wrap);
  });

  function initAutoplay(carousel) {
    if (carousel.dataset.mdAutoplayReady) return;
    carousel.dataset.mdAutoplayReady = 'true';

    const seconds = parseFloat(carousel.dataset.mdAutoplay) || 6;
    const items = () => [...carousel.querySelectorAll('.md-carousel__item:not([hidden])')];
    const dotsHost = document.querySelector(`[data-md-carousel-dots="${carousel.id}"]`);
    const pauseButton = document.querySelector(`[data-md-carousel-pause="${carousel.id}"]`);
    let timer = null;
    let visible = false;
    let hovered = false;
    let focused = false;
    let userPausedUntil = 0;
    let stopped = reducedMotion.matches;

    const navHost = document.querySelector(`[data-md-carousel-prev="${carousel.id}"]`)?.closest('.md-testimonials__nav');

    function offsets() {
      const pad = parseFloat(getComputedStyle(carousel).scrollPaddingInlineStart) || 0;
      const origin = carousel.getBoundingClientRect().left - carousel.scrollLeft + pad;
      const max = carousel.scrollWidth - carousel.clientWidth;
      const stops = items().map((item) => Math.round(Math.max(0, Math.min(max, item.getBoundingClientRect().left - origin))));
      return stops.filter((stop, index) => stops.indexOf(stop) === index);
    }

    function scrollable() {
      return carousel.scrollWidth - carousel.clientWidth > 8;
    }

    function syncControls() {
      const canScroll = scrollable();
      if (navHost) navHost.hidden = !canScroll;
      if (dotsHost) dotsHost.hidden = !canScroll;
      return canScroll;
    }

    function currentIndex() {
      const stops = offsets();
      let best = 0;
      stops.forEach((stop, index) => {
        if (Math.abs(stop - carousel.scrollLeft) < Math.abs(stops[best] - carousel.scrollLeft)) best = index;
      });
      return best;
    }

    function goTo(index) {
      const stops = offsets();
      if (!stops.length) return;
      const target = stops[(index + stops.length) % stops.length];
      carousel.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    function next() {
      const max = carousel.scrollWidth - carousel.clientWidth;
      if (carousel.scrollLeft >= max - 4) goTo(0);
      else goTo(currentIndex() + 1);
    }

    function canPlay() {
      return (
        scrollable() &&
        !stopped &&
        visible &&
        !hovered &&
        !focused &&
        !document.hidden &&
        Date.now() > userPausedUntil &&
        !carousel.querySelector('.is-expanded')
      );
    }

    function tick() {
      if (canPlay()) next();
    }

    function start() {
      clearInterval(timer);
      timer = setInterval(tick, seconds * 1000);
      carousel.style.setProperty('--md-autoplay-duration', `${seconds}s`);
    }

    function nudge() {
      userPausedUntil = Date.now() + seconds * 1500;
    }

    function renderDots() {
      syncControls();
      if (!dotsHost) return;
      const count = offsets().length;
      dotsHost.innerHTML = '';
      for (let index = 0; index < count; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'md-carousel-dot';
        dot.setAttribute('aria-label', `Show testimonial ${index + 1} of ${count}`);
        dot.addEventListener('click', () => {
          nudge();
          goTo(index);
        });
        dotsHost.append(dot);
      }
      updateDots();
    }

    let frame = 0;
    function updateDots() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!dotsHost) return;
        const dots = [...dotsHost.children];
        const active = currentIndex();
        dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
      });
    }

    function setPaused(paused) {
      stopped = paused;
      if (pauseButton) {
        pauseButton.setAttribute('aria-pressed', String(paused));
        pauseButton.setAttribute('aria-label', paused ? 'Play testimonials' : 'Pause testimonials');
        const icon = pauseButton.querySelector('.md-icon');
        if (icon) icon.textContent = paused ? 'play_arrow' : 'pause';
      }
    }

    carousel.addEventListener('pointerenter', () => (hovered = true));
    carousel.addEventListener('pointerleave', () => (hovered = false));
    carousel.addEventListener('focusin', () => (focused = true));
    carousel.addEventListener('focusout', () => (focused = false));
    carousel.addEventListener('pointerdown', nudge, { passive: true });
    carousel.addEventListener('wheel', nudge, { passive: true });
    carousel.addEventListener('touchstart', nudge, { passive: true });
    carousel.addEventListener('scroll', updateDots, { passive: true });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-md-carousel-prev], [data-md-carousel-next]');
      if (!button) return;
      const id = button.getAttribute('data-md-carousel-prev') || button.getAttribute('data-md-carousel-next');
      if (id === carousel.id) nudge();
    });

    if (pauseButton) {
      if (reducedMotion.matches) pauseButton.hidden = true;
      pauseButton.addEventListener('click', () => setPaused(!stopped));
    }

    new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
      },
      { threshold: 0.4 }
    ).observe(carousel);

    renderDots();
    new ResizeObserver(() => renderDots()).observe(carousel);
    new MutationObserver(renderDots).observe(carousel, { attributes: true, subtree: true, attributeFilter: ['hidden'] });
    setPaused(stopped);
    start();
  }

  function init(root = document) {
    initClamps(root);
    root.querySelectorAll('[data-md-autoplay]').forEach(initAutoplay);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  if (document.fonts) document.fonts.ready.then(() => document.querySelectorAll('[data-md-clamp]').forEach(updateClamp));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
